import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Time } from '../time/time';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class DateValidators {
  /**
   * Validates date format, skip if control is pristine
   * @param control Form control containing date value
   * @returns dateFormat error, ortherwise null
   */
  public static dateFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (control.pristine) {
      return null;
    }
    control.markAsTouched();
    return DateValidators.isDateFormat(control) ? null : { dateFormat: true };
  }

  /**
   * Validates dates order between 2 form control. Skip validation if all controls are pristine.
   * RG: Begin date must be equal or before end date.
   * @param beginDateName Name (formControlName attribute) of the begin date control
   * @param endDateName Name (formControlName attribute) of the end date control
   * @param beginDateSubformName Control sub group. No sub group if undefined.
   * @param endDateSubformName Control sub group. No sub group if undefined.
   * @param toTheDay If true, then compare to the day, then to the month. True by default.
   * @param marker Name of the marker in form errors
   * @returns datesOrder error (form level error), otherwise null
   */
  public static datesOrderValidator(
    beginDateName: string,
    endDateName: string,
    beginDateSubformName?: string,
    endDateSubformName?: string,
    toTheDay: boolean = true,
    marker: string = 'datesOrder',
  ): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const beginCtrl: AbstractControl = (beginDateSubformName ? formGroup.controls[beginDateSubformName] : formGroup).get(beginDateName);
      const endCtrl: AbstractControl = (endDateSubformName ? formGroup.controls[endDateSubformName] : formGroup).get(endDateName);

      if (beginCtrl.pristine && endCtrl.pristine) {
        return null;
      }
      beginCtrl.markAsTouched();
      endCtrl.markAsTouched();

      if (beginCtrl?.value && endCtrl?.value && DateValidators.isDateFormat(beginCtrl) && DateValidators.isDateFormat(endCtrl)) {
        // If toTheDay is false, then comparing only to the month (and do not deal with time).
        const begin: Date = toTheDay ? new Date(beginCtrl.value) : new Date(beginCtrl.value.getFullYear(), beginCtrl.value.getMonth(), 1);
        const end: Date = toTheDay ? new Date(endCtrl.value) : new Date(endCtrl.value.getFullYear(), endCtrl.value.getMonth(), 1);

        if (begin > end) {
          return { [marker]: true };
        }
      }
      return null;
    };
  }

  /**
   * Validates dates order between date value and a form control. Skip validation if all controls are pristine.
   * RG: Begin date must be equal or before end date.
   * @param beginDateName Name (formControlName attribute) of the begin date control
   * @param dateValue Date value
   * @returns datesOrderValue error (form level error), otherwise null
   */
  public static datesOrderValidatorValue(beginDateName: string, dateValue: Date): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const beginCtrl: AbstractControl = formGroup.get(beginDateName);

      if (!beginCtrl) {
        return null;
      }

      if (beginCtrl.pristine) {
        return null;
      }
      beginCtrl.markAsTouched();

      if (beginCtrl?.value && dateValue && DateValidators.isDateFormat(beginCtrl)) {
        if (beginCtrl.value < dateValue) {
          return { datesOrderValue: true };
        }
      }
      return null;
    };
  }
  /**
   * Validates dates order between date value and a form control. Skip validation if all controls are pristine.
   * RG: Begin date first stage must be greater then orientation begin date.
   * @param dateString string
   * @param currentDate date
   * @returns currentDateExceeded error (form level error), otherwise null
   */
  public static pastDateValidator(currentDate: Date): (control: FormControl) => ValidationErrors | null {
    return (control: FormControl): ValidationErrors | null => {
      if (control?.value && currentDate && DateValidators.isDateFormat(control.value)) {
        if (moment(control.value).isAfter(currentDate, 'day')) {
          return { currentDateExceeded: true };
        }
      }
      return null;
    };
  }

  /**
   * Validates times order between 2 form control. Skip validation if all controls are pristine.
   * RG: If dates are equals, beginTime must be before or equal endTime.
   * @param beginDateName Name (formControlName attribute) of the begin date control
   * @param endDateName Name (formControlName attribute) of the end date control
   * @param beginTimeName Name (formControlName attribute) of the begin time control
   * @param endTimeName Name (formControlName attribute) of the end time control
   * @returns timesOrder error (form level error), otherwise null
   */
  public static timesOrderValidator(beginTimeName: string, endTimeName: string): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const beginTimeCtrl: AbstractControl = formGroup.get(beginTimeName);
      const endTimeCtrl: AbstractControl = formGroup.get(endTimeName);

      if (beginTimeCtrl.pristine && endTimeCtrl.pristine) {
        return null;
      }
      beginTimeCtrl.markAsTouched();
      endTimeCtrl.markAsTouched();

      if (beginTimeCtrl && beginTimeCtrl.value != null && endTimeCtrl && endTimeCtrl.value != null) {
        const beginTime: Time = beginTimeCtrl.value as Time;
        const endTime: Time = endTimeCtrl.value as Time;

        if (beginTime.hour > endTime.hour || (beginTime.hour == endTime.hour && beginTime.minute > endTime.minute)) {
          return { timesOrder: true };
        }
      }
      return null;
    };
  }

  private static isDateFormat(control: AbstractControl): boolean {
    if (control && control.value == null) {
      return true;
    }
    const dateValue: Date = control.value as Date;
    return dateValue.getTime() && dateValue.getFullYear() < 2500 && dateValue.getFullYear() > 1900;
  }

  public static sameYearValidator(entryDateName: string, yearName: string): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const entryDateCtrl: AbstractControl = formGroup.get(entryDateName);
      const yearCtrl: AbstractControl = formGroup.get(yearName);

      if (entryDateCtrl?.pristine && yearCtrl?.pristine) {
        return null;
      }
      if (yearCtrl.errors) {
        return null;
      }

      if (entryDateCtrl?.errors && !entryDateCtrl?.errors.notSameYear) {
        return null;
      }

      entryDateCtrl?.markAsTouched();
      yearCtrl.markAsTouched();

      if (entryDateCtrl?.value && yearCtrl?.value) {
        const entryDate: Date = entryDateCtrl.value as Date;
        const year: number = +yearCtrl.value;
        if (entryDate.getFullYear() !== year) {
          return { notSameYear: true };
        }
      }
      return null;
    };
  }

  /**
   * A generic validator that:
   * - Takes a "dateControlName"
   * - Iterates over all other controls in the FormGroup
   * - If "dateControlName" is empty but at least one other control has a non-empty value, it returns an error
   *
   * @param dateControlName Name of the date control
   * @param errorKey Name of the error to return
   */
  public static blockFieldsButNoDateGeneric(dateControlName: string, errorKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      // Ensure the given AbstractControl is a FormGroup
      if (!(group instanceof FormGroup)) {
        return null;
      }

      // Retrieve the value of the date control
      const dateControl = group.get(dateControlName);
      const dateValue = dateControl?.value;

      // Build an array of all other controls in the FormGroup (excluding the date control itself)
      const otherControlNames = Object.keys(group.controls).filter((name) => name !== dateControlName);

      // Check if at least one of the other controls is filled
      const anyFieldFilled = otherControlNames.some((ctrlName) => {
        const control = group.get(ctrlName);
        if (!control) {
          return false;
        }
        const value = control.value;
        // We consider a field “filled” if it’s neither null, undefined, nor an empty string
        return value !== null && value !== undefined && value !== '';
      });

      // If at least one field is filled AND the date is empty => return an error
      if (!dateValue && anyFieldFilled) {
        return { [errorKey]: true };
      }

      // Otherwise, validation is fine
      return null;
    };
  }
}
